import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import React from 'react';

import * as benchlingUtil from 'BenchlingUtil';
import {ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import HoverIcon from 'nav/expanded/HoverIcon';
import ReviewConstants from 'notebook/reviews/ReviewConstants';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {apiIdToIconName} from 'util/APIUtil';
import {BasicEvent} from 'util/Types';
import StageRunMenu from 'workflows/components/StageRunMenu';
import {isStageEntryLocked} from 'workflows/stage-entries/StageEntryUtil';
import {STAGE_RUN_RADIUS} from 'workflows/visualization/GraphLayoutConstants';
import {HighlightMode} from 'workflows/visualization/StageFlowNodeHighlightUtil';
import {StageRunCircle} from 'workflows/visualization/StageRunVisualization';
import {
  STAGE_ENTRY_FULL_SHAPE,
  STAGE_RUN_FULL_SHAPE,
  StageEntryDocument,
  StageRun,
} from 'workflows/WorkflowConstants';

interface StageRunDescriptionProps {
  isHovered: boolean;
  onEditStageRun: (stageRun: StageRun) => void;
  onCopySelectedPath: () => void;
  onOpenSetExperimentalCondition: (editingStageRun: StageRun) => void;
  openSetExperimentalConditionDisabled: boolean;
  stageEntry: StageEntryDocument;
  hasImportant: boolean;
  stageRun: StageRun;
  isStageEditable: boolean;
  isWorkflowEditable: boolean;
}

interface State {
  isDropdownOpen: boolean;
}

class StageRunDescription extends React.Component<StageRunDescriptionProps, State> {
  static propTypes = {
    isHovered: PropTypes.bool.isRequired,
    onEditStageRun: PropTypes.func.isRequired,
    onCopySelectedPath: PropTypes.func.isRequired,
    onOpenSetExperimentalCondition: PropTypes.func.isRequired,
    openSetExperimentalConditionDisabled: PropTypes.bool.isRequired,
    stageEntry: STAGE_ENTRY_FULL_SHAPE.isRequired,
    hasImportant: PropTypes.bool.isRequired,
    stageRun: STAGE_RUN_FULL_SHAPE.isRequired,
    isStageEditable: PropTypes.bool.isRequired,
    isWorkflowEditable: PropTypes.bool.isRequired,
  };

  override state: State = {
    isDropdownOpen: false,
  };

  _handleOpenDropdown = (): void => {
    this.setState({isDropdownOpen: true});
  };

  _handleCloseDropdown = (): void => {
    this.setState({isDropdownOpen: false});
  };

  _handleEditStageRun = (): void => {
    this.props.onEditStageRun(this.props.stageRun);
  };

  _handleOpenSetExperimentalCondition = (): void => {
    this.props.onOpenSetExperimentalCondition(this.props.stageRun);
  };

  _handleEntryClick = (e: BasicEvent): void => {
    benchlingUtil.navigate(e);
  };

  override render(): JSX.Element {
    const stageRunStatus = this.props.stageRun.status;
    const stageEntry = this.props.stageEntry;
    return (
      <div className="stageRunDescription u-lr-split">
        <div className="u-min-width-zero">
          <div className="stageRunDescription-header">
            <svg width={2 * STAGE_RUN_RADIUS} height={2 * STAGE_RUN_RADIUS}>
              {/* Subtract 1 from radius for the border */}
              <StageRunCircle
                highlightMode={HighlightMode.DEFAULT}
                isHovered={this.props.isHovered}
                position={{x: STAGE_RUN_RADIUS, y: STAGE_RUN_RADIUS}}
                radius={STAGE_RUN_RADIUS - 1}
                stageRunStatus={stageRunStatus}
              />
            </svg>
            <div className="stageRunDescription-name">{this.props.stageRun.name}</div>
          </div>
          <div className="stageRunDescription-entry u-ellipsis-text">
            <a onClick={this._handleEntryClick} href={this.props.stageEntry.editURL}>
              <BenchlingIcon
                className="u-tiny-spacing-right"
                icon={apiIdToIconName(this.props.stageEntry.id)}
              />
              <span className="u-tiny-spacing-right">{this.props.stageEntry.name}</span>
              {this.props.hasImportant && (
                <BenchlingIcon icon={ReviewStatus.REJECTED} className="u-dark-warning-color" />
              )}
            </a>
            <span className="u-success-color u-tiny-spacing-left">
              {
                // eslint-disable-next-line benchlint/no-in-operator
                'review' in stageEntry &&
                  stageEntry.review &&
                  stageEntry.review.status === ReviewConstants.ACCEPTED &&
                  'Reviewed'
              }
            </span>
          </div>
        </div>
        {this.props.stageEntry.permissions.writable && !isStageEntryLocked(this.props.stageEntry) && (
          <AbsoluteDropdownContainer
            menu={
              <StageRunMenu
                onCopySelectedPath={this.props.onCopySelectedPath}
                onEditStageRun={this._handleEditStageRun}
                onOpenSetExperimentalCondition={this._handleOpenSetExperimentalCondition}
                openSetExperimentalConditionDisabled={this.props.openSetExperimentalConditionDisabled}
                stageRun={this.props.stageRun}
                isStageEditable={this.props.isStageEditable}
                isWorkflowEditable={this.props.isWorkflowEditable}
              />
            }
            onRequestClose={this._handleCloseDropdown}
            open={this.state.isDropdownOpen}
            shouldCloseOnInsideClick={true}
          >
            <HoverIcon
              className={classNames('workflowDetailsTable-hoverIcon', {
                'is-active': this.state.isDropdownOpen,
              })}
              icon="chevron-down"
              onClick={this._handleOpenDropdown}
            />
          </AbsoluteDropdownContainer>
        )}
      </div>
    );
  }
}

export default StageRunDescription;
